import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 型定義 ---

export type CreateSearchConditionParams = {
  conditionKey: string;
  conditionName: string;
  conditionValue: string;
};

export type DeleteSearchConditionParams = {
  searchConditionId: string;
};

export type SetDefaultSearchConditionParams = {
  searchConditionId: string;
};

export type UnsetDefaultSearchConditionParams = {
  searchConditionId: string;
};

export type SearchConditionResult = {
  searchConditionId: string;
  conditionKey: string;
  conditionName: string;
  conditionValue: string;
  /** デフォルト検索条件フラグ（API8101 v2 で追加。condition_key 単位で最大1件がtrue） */
  isDefault: boolean;
};

// --- API関数 ---

/**
 * 検索条件取得API
 * @param conditionKey - 条件キー（必須, 最大100文字, 画面名）
 */
export const listSearchCondition = async (
  conditionKey: string,
): Promise<SearchConditionResult[]> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.get(`${KanzhongBackend.LIST_SEARCH_CONDITION}/${conditionKey}`, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 検索条件登録API
 * @param params.conditionKey - 条件キー（必須, 最大100文字）
 * @param params.conditionName - 条件名（必須, 最大50文字）
 * @param params.conditionValue - 条件値（必須, 検索条件のJSON文字列）
 */
export const createSearchCondition = async (
  params: CreateSearchConditionParams,
): Promise<string> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.CREATE_SEARCH_CONDITION, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.searchConditionId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 検索条件削除API
 * @param params.searchConditionId - 検索条件ID（必須, 最大64文字, UUID）
 */
export const deleteSearchCondition = async (params: DeleteSearchConditionParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.DELETE_SEARCH_CONDITION, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * デフォルト検索条件設定API（API8104）
 * @param params.searchConditionId - 検索条件ID（必須, 最大64文字, UUID）
 */
export const setDefaultSearchCondition = async (
  params: SetDefaultSearchConditionParams,
): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.SET_DEFAULT_SEARCH_CONDITION, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * デフォルト検索条件解除API（API8105）
 * @param params.searchConditionId - 検索条件ID（必須, 最大64文字, UUID）
 */
export const unsetDefaultSearchCondition = async (
  params: UnsetDefaultSearchConditionParams,
): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UNSET_DEFAULT_SEARCH_CONDITION, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
